import { useEffect, useRef, useState } from 'react';

import TrueCalendar from 'utils/TrueCalendar';

const formatTime = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${String(h).padStart(2, '0')}:${mm}:${ss}` : `00:${mm}:${ss}`;
};

const getNowMs = () =>
  TrueCalendar.isSynced ? TrueCalendar.getCurrentTime() : Date.now();

const useTimer = ({ cardId, mineCycleStartedAt, miningTime, onStart, onEnd } = {}) => {
  const [timeDisplay, setTimeDisplay] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);
  const onStartRef = useRef(onStart);
  const onEndRef = useRef(onEnd);

  useEffect(() => { onStartRef.current = onStart; }, [onStart]);
  useEffect(() => { onEndRef.current = onEnd; }, [onEnd]);

  useEffect(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setTimeDisplay('');
    setIsRunning(false);

    if (!mineCycleStartedAt || !miningTime) {
      onEndRef.current?.();
      return;
    }

    const startMs = new Date(mineCycleStartedAt).getTime();
    const endMs = startMs + miningTime * 60 * 1000;
    let remainingSeconds = Math.max(0, Math.floor((endMs - getNowMs()) / 1000));

    if (remainingSeconds <= 0) {
      onEndRef.current?.();
      return;
    }

    onStartRef.current?.();
    setTimeDisplay(formatTime(remainingSeconds));
    setIsRunning(true);

    intervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.floor((endMs - getNowMs()) / 1000));
      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setTimeDisplay('00:00:00');
        setIsRunning(false);
        onEndRef.current?.();
      } else {
        setTimeDisplay(formatTime(remaining));
      }
    }, 1000);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  // cardId ensures a fresh timer starts when switching to a different card
  // even if it happens to share the same mineCycleStartedAt / miningTime values
  }, [cardId, mineCycleStartedAt, miningTime]);

  return { timeDisplay, isRunning };
};

export default useTimer;
